import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ScrollText, Search, Loader2, ShieldAlert, X } from "lucide-react";
import { zoomCard } from "@/components/CardZoom";
import { toast } from "sonner";

const FILTERS = [
  ["all", "Everything"], ["mint", "Mints"], ["exclusive", "Exclusives"], ["pack_open", "Pack Rips"],
  ["gift", "Gifts & Transfers"], ["sale", "Sales"], ["listed", "Listings"], ["royalty", "Royalties"],
  ["bounty_set", "Bounties Set"], ["bounty_win", "Bounties Won"], ["set_claim", "Set Claims"], ["autograph", "Autographs"],
  ["recall", "Recalls"],
];

const KIND_COLORS = {
  mint: "border-zinc-400 text-zinc-200", pack_open: "border-pb-cyan text-pb-cyan",
  gift: "border-fuchsia-400 text-fuchsia-300", sale: "border-emerald-400 text-emerald-300",
  listed: "border-sky-400 text-sky-300", royalty: "border-lime-400 text-lime-300",
  bounty_set: "border-orange-400 text-orange-300", bounty_win: "border-yellow-400 text-yellow-300",
  set_claim: "border-teal-400 text-teal-300", autograph: "border-amber-400 text-amber-300",
  recall: "border-red-500 text-red-400",
};

function HoldingsModal({ cardId, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { setData((await api.get(`/vault/admin/card/${cardId}/holdings`)).data); }
    catch { toast.error("Couldn't load card holdings"); onClose(); }
  };
  useEffect(() => { load(); }, [cardId]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  if (!data) return null;
  const { card, copies, pool_available } = data;
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="holdings-modal" onClick={onClose}>
      <div className="bg-pb-surface border border-pb-border max-w-lg w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <span className="font-display font-bold uppercase flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400" /> House Controls — {card.name}
          </span>
          <button data-testid="holdings-close" onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex gap-3 mb-4">
          <img src={card.url} alt="" className="w-16 aspect-[3/4] object-contain bg-black border border-pb-border" />
          <div className="text-xs text-zinc-400 space-y-1">
            <div className="uppercase tracking-widest font-bold text-zinc-200">{card.rarity}{card.one_of_one ? " · 1/1" : card.edition_total ? ` · /${card.edition_total}` : ""}</div>
            {card.edition_total ? <div>{card.edition_claimed} of {card.edition_total} numbered copies claimed</div> : null}
            {card.one_of_one && <div>{card.pulled ? "The 1/1 has been pulled" : "The 1/1 is still in the pool"}</div>}
            <div className={pool_available ? "text-lime-300" : "text-zinc-600"}>{pool_available ? "Available in pool packs" : "Not available in the pool"}</div>
          </div>
        </div>
        {pool_available && (
          <button data-testid="claim-from-pool-btn" disabled={busy}
            onClick={() => act(() => api.post(`/vault/admin/pool/${card.id}/claim`), "Copy claimed into your collection")}
            className="w-full mb-4 border border-lime-400/70 text-lime-300 font-bold uppercase tracking-widest text-[10px] py-2.5 hover:bg-lime-400/10 active:scale-95 transition-all disabled:opacity-40">
            Claim a Copy from the Pool → My Collection
          </button>
        )}
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold mb-2">Copies in circulation ({copies.length})</div>
        <div className="space-y-1.5">
          {copies.map((c) => (
            <div key={c.id} className="flex items-center gap-2 border border-pb-border/60 px-3 py-2 text-xs" data-testid={`holding-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <span className="text-zinc-200 font-semibold">{c.owner_name || "—"}</span>
                <span className="text-zinc-500"> · {c.one_of_one ? "1/1" : c.edition_num ? `#${c.edition_num}/${c.edition_total}` : "standard"}{c.grade_label ? ` · ${c.grade_label}` : ""}</span>
                {c.recalled_at && <span className="text-red-400"> · recalled</span>}
              </div>
              <button data-testid={`recall-copy-${c.id}`} disabled={busy}
                onClick={() => {
                  if (!window.confirm(`Recall this copy from ${c.owner_name}? It moves to YOUR collection and they'll be notified.`)) return;
                  act(() => api.post(`/vault/admin/copies/${c.id}/recall`), `Copy recalled from ${c.owner_name}`);
                }}
                className="shrink-0 border border-red-500/60 text-red-400 font-bold uppercase tracking-widest text-[9px] px-2.5 py-1 hover:bg-red-500/10 active:scale-95 transition-all disabled:opacity-40">
                Recall
              </button>
            </div>
          ))}
          {copies.length === 0 && <div className="text-xs text-zinc-600 py-2">No copies out yet — the card only lives in the pool.</div>}
        </div>
      </div>
    </div>
  );
}

export default function RegistryPanel() {
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("all");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [manageId, setManageId] = useState(null);

  const load = async (t = type, query = q, skip = 0, append = false) => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/registry", { params: { type: t, q: query, skip, limit: 50 } });
      setRows((r) => (append ? [...r, ...data.rows] : data.rows));
      setHasMore(data.has_more);
    } catch { /* noop */ }
    finally { setBusy(false); }
  };

  useEffect(() => { load("all", ""); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="border border-pb-border bg-pb-surface p-5" data-testid="registry-panel">
      <div className="flex items-center gap-2 mb-1"><ScrollText className="w-4 h-4 text-lime-300" /><span className="font-display font-bold uppercase">The Registry — Master Card Ledger</span></div>
      <p className="text-xs text-zinc-500 mb-4">
        Every event in the card ecosystem, nothing hidden: every mint, exclusive, pack rip, gift, sale, royalty, bounty,
        set claim and autograph step — timestamped and searchable.
      </p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {FILTERS.map(([k, label]) => (
          <button key={k} data-testid={`registry-filter-${k}`}
            onClick={() => { setType(k); load(k, q); }}
            className={`text-[9px] font-black uppercase tracking-widest px-2.5 py-1.5 border transition-colors ${type === k ? "border-lime-400 text-lime-300 bg-lime-400/10" : "border-pb-border text-zinc-500 hover:border-zinc-500"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-2 bg-pb-elevated border border-pb-border px-3 mb-3">
        <Search className="w-3.5 h-3.5 text-zinc-500" />
        <input data-testid="registry-search" value={q} onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(type, q)}
          placeholder="Search names, cards, notes… (Enter)"
          className="flex-1 bg-transparent py-2 text-sm text-white focus:outline-none" />
      </div>
      <div className="max-h-[480px] overflow-y-auto divide-y divide-pb-border/40" data-testid="registry-rows">
        {rows.map((r, i) => (
          <div key={i} className="flex items-start gap-3 py-2" data-testid={`registry-row-${i}`}>
            {r.url && <img src={r.url} alt="" className="w-7 aspect-[3/4] object-contain bg-black border border-pb-border shrink-0 cursor-zoom-in" loading="lazy"
              title="Tap to zoom" onClick={() => zoomCard(r.url, r.user_name)} />}
            <span className={`shrink-0 mt-0.5 text-[8px] font-black uppercase tracking-widest border px-1.5 py-0.5 ${KIND_COLORS[r.kind] || "border-zinc-600 text-zinc-400"}`}>
              {r.kind.replace("_", " ")}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-zinc-300 leading-snug">{r.note}</div>
              <div className="text-[9px] text-zinc-600 uppercase tracking-widest">
                {r.who || "—"} · {(r.at || "").replace("T", " ").slice(0, 19)}
              </div>
            </div>
            {r.coins > 0 && <span className="shrink-0 text-[10px] font-mono font-bold text-lime-300">{r.coins} BLC</span>}
            {r.kind === "mint" && r.card_id && (
              <button data-testid={`registry-manage-${i}`} onClick={() => setManageId(r.card_id)}
                className="shrink-0 border border-red-500/50 text-red-400 font-bold uppercase tracking-widest text-[8px] px-2 py-1 hover:bg-red-500/10 active:scale-95 transition-all">
                Manage
              </button>
            )}
          </div>
        ))}
        {rows.length === 0 && !busy && <div className="py-6 text-xs text-zinc-600 text-center">No registry entries match.</div>}
      </div>
      <div className="flex items-center gap-3 mt-3">
        {hasMore && (
          <button data-testid="registry-load-more" onClick={() => load(type, q, rows.length, true)} disabled={busy}
            className="text-[9px] font-black uppercase tracking-widest border border-pb-border px-4 py-2 text-zinc-300 hover:border-lime-400 hover:text-lime-300 disabled:opacity-50">
            Load more
          </button>
        )}
        {busy && <Loader2 className="w-4 h-4 animate-spin text-lime-300" />}
        <span className="text-[9px] text-zinc-600 uppercase tracking-widest ml-auto">{rows.length} entries shown</span>
      </div>
      {manageId && <HoldingsModal cardId={manageId} onClose={() => setManageId(null)} />}
    </div>
  );
}
